import {useSearchParam} from '@raison/navigation';
import {Decoder} from '@raison/prelude';
import currencyIconSrc from '@raison/static/icons/coins-line.svg';
import touchIconSrc from '@raison/static/icons/fingerprint-line.svg';
import shieldIcon from '@raison/static/icons/shield-check-line.svg';
import {RootLayout, RsnButton, RsnDialog, RsnIconWidget, RsnPicker, RsnSwitch} from '@raison/ui-kit';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Route, Routes, useNavigate} from 'react-router';
import {usePrimaryCurrencySetter} from '~/entities/currencies';
import {CommonLoaderDialog} from '~/entities/dialogs';
import {
    deActivateBiometry,
    get_biometry_type,
    get_touch_id_availability,
    gonative_delete_secret,
    isBiometryActivated,
    useUserInfoQueryOptions,
} from '~/entities/user';
import {BaseCurrencyCode, Source} from '~/library/types';
import {setCurrentCurrency} from '~/redux/assets/assetsActions';
import {selectCurrentCurrency} from '~/redux/assets/assetsSelectors';
import {CURRENCIES} from '~/shared/constants/currencies';
import defaultIcon from '~/shared/legacy_res/img/svg/default.svg';
import {appLocalStorage} from '~/shared/persistence';
import {getAppLink, isMobileApp} from '~/shared/utils/mobileDeviceDetect';
import {AuthenticationSetting} from './AuthenticationSetting';
import {BrokerAccountButton, WithBrokerAccountService} from './BrokerAccountButton';
import PasswordForm from './PasswordForm';
import faceIconSrc from './imgs/faceId.svg';

export function SettingsPage(props: WithBrokerAccountService) {
    const [isBiometryOn, setBiometrySwitch] = useState(isBiometryActivated);
    const navigate = useNavigate();

    const [dialogParam, setDialogParam] = useSearchParam('dialog');

    const [biometryType, setBiometryType] = useState(get_biometry_type);
    const [loaderReason, setLoaderReason] = useState<null | string>(null);

    const qc = useQueryClient();
    const userInfoOpt = useUserInfoQueryOptions();
    const {data: {otpEnabled: otpIsOn = false} = {}, isFetching: userIsFetching} = useQuery({
        ...userInfoOpt,
        refetchOnMount: true,
    });

    const toggleCurrencyModal = () => {
        setDialogParam('currency');
    };

    const toggleAuthenticatorApp = async () => {
        const {otpEnabled} = await qc.ensureQueryData(userInfoOpt);
        if (!otpEnabled) {
            navigate('authenticator-on');
        } else {
            navigate('authenticator-off');
        }
    };

    const toggleBiometrySwitch = () => {
        if (!isMobileApp()) {
            setDialogParam('warning');
            return;
        }

        if (isBiometryOn) {
            deActivateBiometry();
            gonative_delete_secret();
            setBiometrySwitch(false);
            return;
        }

        // if biometry is not available
        get_touch_id_availability(); // It takes some time (500ms)

        setTimeout(() => {
            if (appLocalStorage.hasTouchId.get()) {
                navigate('biometry');
            } else {
                setDialogParam('error'); // this case if biometric on the phone is not activated
            }
        }, 500);
    };

    useEffect(() => {
        appLocalStorage.dataBiometry.remove();

        if (appLocalStorage.runBiometryActivation.get() === 'start') {
            appLocalStorage.runBiometryActivation.remove();
            deActivateBiometry();
            gonative_delete_secret();
        }

        setBiometrySwitch(isBiometryActivated());
        setBiometryType(get_biometry_type());
    }, []);

    const currentCurrency = useSelector(selectCurrentCurrency);

    return (
        <RootLayout title='Settings'>
            <Routes>
                <Route index element={renderSettings()} />
                <Route path='biometry' element={renderPasswordForm()} />
                <Route path='authenticator-on' element={<AuthenticationSetting.On pathBack='..' />} />
                <Route path='authenticator-off' element={<AuthenticationSetting.Off pathBack='..' />} />
            </Routes>

            <RsnDialog.Controller onClose={() => navigate(-1)}>
                {(() => {
                    switch (dialogParam) {
                        case 'warning':
                            return <WarningDialog />;
                        case 'error':
                            return <ErrorDialog />;
                        case 'currency':
                            return <PrimaryCurrencyModal />;
                    }
                })()}
            </RsnDialog.Controller>

            <RsnDialog.Controller onClose={() => setLoaderReason(null)}>
                {!!loaderReason && <CommonLoaderDialog reason={loaderReason} />}
            </RsnDialog.Controller>
        </RootLayout>
    );

    function renderPasswordForm(): React.ReactNode {
        return (
            <div className={`flex flex-1 flex-col justify-between p-4`}>
                <PasswordForm
                    changeLoaderState={visible => setLoaderReason(visible ? 'Activating biometrics...' : null)}
                />
            </div>
        );
    }

    function renderSettings() {
        return (
            <div className='flex flex-1 flex-col px-4 pb-4 pt-2'>
                <div className='flex flex-col divide-y divide-solid divide-neutral-30 *:py-4'>
                    {renderSettingsSwitch({
                        onToggle: toggleBiometrySwitch,
                        isOn: isBiometryOn,
                        icon: biometryType === 'faceId' ? faceIconSrc : touchIconSrc,
                        heading: biometryType === 'faceId' ? 'Use Face ID' : 'Use Touch ID',
                        details: 'Log in using biometrics',
                    })}
                    {renderSettingsSwitch({
                        onToggle: toggleAuthenticatorApp,
                        disabled: userIsFetching,
                        isOn: otpIsOn,
                        icon: shieldIcon,
                        heading: 'Authenticator app confirmation',
                        details: 'Confirm transactions using a one-time code from authenticator app',
                    })}
                    {renderSettingsTrigger({
                        onClick: toggleCurrencyModal,
                        value: currentCurrency,
                        icon: currencyIconSrc,
                        heading: 'Primary currency',
                        details: 'Assets equivalent',
                    })}
                </div>

                <div className='mt-auto'>
                    <BrokerAccountButton {...props.brokerAccount} />
                </div>
            </div>
        );
    }
}

function renderSettingsTrigger({
    onClick,
    value,
    icon,
    heading,
    afterHeading,
    details,
}: {
    onClick: () => void;
    value: string;
    icon: string;
    heading: React.ReactNode;
    afterHeading?: React.ReactNode;
    details: React.ReactNode;
}) {
    return (
        <RsnIconWidget.Lg
            as='button'
            role='button'
            icon={RsnIconWidget.framedIcon(icon)}
            addon={<RsnIconWidget.Arrow label={value} />}
            onClick={onClick}
        >
            <RsnIconWidget.TextBlock heading={heading} afterHeading={afterHeading} details={details} />
        </RsnIconWidget.Lg>
    );
}

function renderSettingsSwitch({
    onToggle,
    isOn,
    icon,
    disabled,
    heading,
    afterHeading,
    details,
}: {
    onToggle: () => void;
    isOn: boolean;
    icon: string;
    disabled?: boolean;
    heading: React.ReactNode;
    afterHeading?: React.ReactNode;
    details: React.ReactNode;
}) {
    return (
        <RsnIconWidget.Lg
            icon={RsnIconWidget.framedIcon(icon)}
            addon={<RsnSwitch disabled={disabled} checked={isOn} onChange={onToggle} />}
        >
            <RsnIconWidget.TextBlock multiline heading={heading} afterHeading={afterHeading} details={details} />
        </RsnIconWidget.Lg>
    );
}

function WarningDialog() {
    return (
        <RsnDialog.Window>
            <RsnDialog.CenteredHeader
                text="Touch ID  doesn't work in the browser"
                icon={{element: <RsnDialog.StatusIcon status='alert' />}}
            />
            <RsnDialog.Body>
                <div>To activate biometrics, you need to</div>
                <a className='fv-text-l-bold text-primary-30' href={getAppLink()}>
                    {'download the Raison app'}
                </a>
            </RsnDialog.Body>
        </RsnDialog.Window>
    );
}

function ErrorDialog() {
    const {onClose} = RsnDialog.useControls();
    return (
        <RsnDialog.Window>
            <RsnDialog.CenteredHeader
                text='Biometrics unavailable'
                icon={{element: <RsnDialog.StatusIcon status='alert' />}}
            />
            <RsnDialog.Body>
                Check biometrics in phone settings or try to unlock your phone using your passcode.
            </RsnDialog.Body>
            <RsnDialog.Footer>
                <RsnButton.Secondary onClick={onClose}>I got it</RsnButton.Secondary>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}
const defaultIconSrc = Decoder.run(Source, defaultIcon).orThrow();

function PrimaryCurrencyModal() {
    const currentCurrency = useSelector(selectCurrentCurrency);
    const currencyOptions = CURRENCIES.map(acc => ({
        value: acc.currency,
        icon: {src: acc.icon ?? defaultIconSrc},
        title: acc.name,
        hint: acc.currency,
    }));
    const {onClose} = RsnDialog.useControls();
    const dispatch = useDispatch();
    const setPrimaryCurrency = usePrimaryCurrencySetter();
    const handleChange = (currency: BaseCurrencyCode) => {
        setPrimaryCurrency(currency);
        dispatch(setCurrentCurrency(currency));
        onClose?.();
    };

    return (
        <RsnPicker.Dialog
            title={'Primary currency'}
            options={currencyOptions}
            value={currentCurrency}
            onChange={handleChange}
        />
    );
}
